import React from 'react';
import { useObserver } from 'mobx-react-lite';
import _ from 'lodash';
import { useQuery } from '@apollo/client';

import { useStores } from 'src/hooks/use-stores';
import { MenuSectionType } from 'types/graphql';

import { Page } from '../components/customize.styles';
import { EmbedYourMenu } from './embed-your-menu';
import { EmbedHomepageSections } from './embed-homepage-sections';
import { EmbedCollectionCarousel } from './embed-collection-carousel';
import MENU_SECTIONS from './get-menu-sections.gql';
import COLLECTIONS_FOR_EMBED from './embed-collection-carousel/get-collections-for-embed.gql';

// Personalized sections require a signed-in shopper's identity/history and have no rendering support in the
// embed surface (embedded-carousel.jsx has no case for them and silently drops them) — exclude both.
const NON_EMBEDDABLE_SECTIONS = [MenuSectionType.recentlyViewed, MenuSectionType.recommendedForYou];

export function Embed() {
  const { UI, apolloClient } = useStores();
  const dispensary = useObserver(() => UI.dispensary);

  const { data } = useQuery(MENU_SECTIONS, {
    variables: { dispensaryId: dispensary?.id },
    client: apolloClient,
  });

  const { data: collectionsData } = useQuery(COLLECTIONS_FOR_EMBED, {
    variables: { dispensaryId: dispensary?.id },
    client: apolloClient,
    skip: !dispensary?.id,
  });

  const menuSections = _.chain(data)
    .get('getMenuSectionsAdmin', [])
    .reject((section) => NON_EMBEDDABLE_SECTIONS.includes(section.sectionType))
    .map((section) => ({
      value: section.label,
      key: section.id,
      ...section,
    }))
    .value();

  // Collections already published to the homepage already have a working embed path via
  // "Embed Homepage Sections" (keyed by MenuSection.id) -- excluding them here avoids listing
  // the same collection twice with two different, equally-valid-looking embed scripts.
  const homepagePublishedCollectionIds = _.chain(data)
    .get('getMenuSectionsAdmin', [])
    .map('collectionId')
    .compact()
    .value();

  const collections = _.chain(collectionsData)
    .get('getCollections', [])
    .filter((collection) => collection.enabled !== false)
    .reject((collection) => homepagePublishedCollectionIds.includes(collection.id))
    .map((collection) => ({
      value: collection.title,
      key: collection.id,
      ...collection,
    }))
    .value();

  const updateValue = (event) => {
    const { name: fieldName, value, type, checked } = event.target;
    if (type === 'checkbox') {
      UI.updateDispensaryProperty(fieldName, checked);
    } else {
      UI.updateDispensaryProperty(fieldName, value);
    }
    UI.unpublishedDispoChanges = true;
    return true;
  };

  return (
    <>
      <Page pb='66px'>
        <EmbedYourMenu updateValue={updateValue} dispensary={dispensary} />
        {!_.isEmpty(menuSections) && (
          <EmbedHomepageSections updateValue={updateValue} dispensary={dispensary} menuSections={menuSections} />
        )}
        {!_.isEmpty(collections) && <EmbedCollectionCarousel dispensary={dispensary} collections={collections} />}
      </Page>
    </>
  );
}
